import { FC, useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { toast } from "react-toastify";
import { InputSelect } from "@funded-here-interface/common";
import useGetExternalAccounts from "../../../hooks/useGetExternalAccounts";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import useGetBalancesListing from "../../../hooks/useGetBalancesListing";
import { BalancesListing as BalanceListing } from "../../../services/getBalancesListing";

const BalancesListing: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  //store pagination state in your own state
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [externalAccountList, setExternalAccountList] = useState<any>([]);

  const [externalAccount, setExternalAccount] = useState("");

  const [dataLoading, setDataLoading] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const { data: fetchExternalAccount } = useGetExternalAccounts(token);

  const { data, error, isLoading } = useGetBalancesListing(
    token,
    externalAccount,
    pagination,
    refreshKey
  );

  if (error) {
    toast.error((error as Error).message);
  }

  useEffect(() => {
    if (fetchExternalAccount && fetchExternalAccount.length > 0) {
      setExternalAccountList(
        fetchExternalAccount.map((item: any) => ({
          label: item.ref,
          value: item.accountId,
        }))
      );
    }
  }, [fetchExternalAccount]);

  const handlePaginationChange = (
    updater:
      | MRT_PaginationState
      | ((old: MRT_PaginationState) => MRT_PaginationState)
  ) => {
    const newPagination =
      typeof updater === "function" ? updater(pagination) : updater;

    if (
      newPagination.pageIndex !== pagination.pageIndex ||
      newPagination.pageSize !== pagination.pageSize
    ) {
      setDataLoading(true);
      setPagination(newPagination);
    }
  };

  useEffect(() => {
    setDataLoading(false);
  }, [data]);

  const totalRowCount = data?.totalRowCount ?? 0;

  const columns = useMemo<MRT_ColumnDef<BalanceListing>[]>(
    () => [
      {
        accessorKey: "currencyName",
        header: "Currency Name",
      },

      {
        accessorKey: "currency",
        header: "Currency",
      },
      {
        accessorKey: "amount",
        header: "Balance Amount",
      },
    ],
    []
  );

  const table = useMantineReactTable<BalanceListing>({
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading: isLoading || dataLoading }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: handlePaginationChange, //hoist pagination state to your state when it changes internally
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
  });

  return (
    <div>
      <div>
        <InputSelect
          onChange={(value: string) => {
            setExternalAccount(value);
            setRefreshKey((prev) => prev + 1);
            setDataLoading(true);
          }}
          labelText="External Account"
          id="externalAccount"
          value={externalAccount}
          data={externalAccountList}
        />
      </div>
      <MantineReactTable table={table} />
    </div>
  );
};

export default BalancesListing;
